import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnalyticsSummaryResponse, FundamentalsResponse } from '@/api/types';
import { fmtScore, FUND_DEFAULT_COLORS } from '@/utils/analytics';
import { OUDrillCard } from './resumen/OUDrillCard';
import { TopBotBehaviors } from './resumen/TopBotBehaviors';
import { OmissionsCard } from './resumen/OmissionsCard';

interface ResumenTabProps {
  apiSummary?: AnalyticsSummaryResponse;
  apiFundamentals?: FundamentalsResponse;
  /** Estado de carga del fetch de fundamentales — controla el fallback explícito (ver MOCK_FALLBACK.md). */
  isLoadingFundamentals?: boolean;
  /** Estado de error del fetch de fundamentales — controla el fallback explícito (ver MOCK_FALLBACK.md). */
  isErrorFundamentals?: boolean;
}

const MEDAL_COLORS = ['#F59E0B', '#9CA3AF', '#CD7F32'];

function scoreColor(v: number): string {
  if (v >= 3.5) return 'var(--cp-success)';
  if (v >= 3.0) return '#10b981';
  if (v >= 2.5) return '#f59e0b';
  return 'var(--cp-danger-light)';
}

export function ResumenTab({
  apiSummary,
  apiFundamentals,
  isLoadingFundamentals = false,
  isErrorFundamentals = false,
}: ResumenTabProps) {
  const { t } = useTranslation();
  const [ouDrillId, setOuDrillId] = useState<string | null>(null);

  // KPIs — 100% desde apiSummary; sin apiSummary, estado vacío explícito (no fallback a mock).
  const kpiAvgScore = apiSummary?.avgScore ?? 0;
  const kpiTotal = apiSummary?.totalEvaluations ?? 0;
  const kpiResponseRate = apiSummary?.responseRate ?? 0;
  const kpiAvgPerDay = apiSummary?.avgPerDay ?? 0;
  const kpiUniqueEvals = apiSummary?.uniqueEvaluators ?? 0;

  // Total de "no observables" derivado de los fundamentales reales (no hay registros individuales en el summary).
  const noObsCount = useMemo(
    () => (apiFundamentals ?? []).reduce((sum, f) => sum + (f.noObs ?? 0), 0),
    [apiFundamentals],
  );

  const KPI_LIST = useMemo(
    () => [
      {
        label: t('analytics.kpi.evaluations'),
        value: kpiTotal.toLocaleString(),
        sub: t('analytics.kpi.ofTotal'),
        icon: 'fa-poll',
        color: '#1D4ED8',
        bg: '#EFF6FF',
        valueColor: undefined as string | undefined,
      },
      {
        label: t('analytics.kpi.avgScore'),
        value: fmtScore(kpiAvgScore),
        sub: t('analytics.kpi.scale'),
        icon: 'fa-star',
        color: 'var(--cp-success)',
        bg: 'var(--cp-success-bg)',
        valueColor: scoreColor(kpiAvgScore),
      },
      {
        label: t('analytics.kpi.responseRate'),
        value: `${kpiResponseRate}%`,
        sub: `${noObsCount} ${t('analytics.kpi.noObservables')}`,
        icon: 'fa-percentage',
        color: '#D97706',
        bg: '#FEF3C7',
        valueColor: undefined as string | undefined,
      },
      {
        label: t('analytics.kpi.avgPerDay'),
        value: String(kpiAvgPerDay),
        sub: t('analytics.kpi.perDay'),
        icon: 'fa-paper-plane',
        color: '#0369A1',
        bg: '#F0F9FF',
        valueColor: undefined as string | undefined,
      },
      {
        label: t('analytics.kpi.evaluators'),
        value: String(kpiUniqueEvals),
        sub: t('analytics.kpi.activeParticipants'),
        icon: 'fa-users',
        color: '#BE185D',
        bg: '#FDF2F8',
        valueColor: undefined as string | undefined,
      },
    ],
    [t, kpiTotal, kpiAvgScore, noObsCount, kpiResponseRate, kpiAvgPerDay, kpiUniqueEvals],
  );

  // Fund positioning — únicamente desde apiFundamentals; sin fallback a mock (MOCK_FALLBACK.md).
  const fundData = useMemo(() => {
    if (!apiFundamentals || apiFundamentals.length === 0) return [];
    return [...apiFundamentals]
      .map((af, i) => ({
        id: af.id,
        name: af.name,
        color: af.color ?? FUND_DEFAULT_COLORS[i % FUND_DEFAULT_COLORS.length]!,
        avg: af.score,
        evalCount: af.evaluations,
        noObsCount: af.noObs ?? 0,
        noObsPct:
          af.evaluations > 0 ? Math.round(((af.noObs ?? 0) / af.evaluations) * 100) : 0,
      }))
      .sort((a, b) => b.avg - a.avg);
  }, [apiFundamentals]);

  const best = fundData[0];
  const worst = fundData[fundData.length - 1];
  const mostNoObs = [...fundData].sort((a, b) => b.noObsPct - a.noObsPct)[0];

  // Mensaje de estado explícito para el bloque de fundamentales — null cuando hay datos que mostrar.
  const fundStateMessage = useMemo(() => {
    if (isLoadingFundamentals) return t('common.loading');
    if (isErrorFundamentals) return t('common.error');
    if (fundData.length === 0) return t('analytics.summary.noOmissions');
    return null;
  }, [isLoadingFundamentals, isErrorFundamentals, fundData, t]);

  // Top / bottom behaviors — directamente desde apiSummary (el backend ya resuelve el nombre).
  const topBehaviors = useMemo(
    () =>
      (apiSummary?.topBehaviors ?? []).map((b, i) => ({
        id: b.id ?? `top-${i}`,
        name: b.name,
        fundId: '',
        fundColor: 'var(--cp-success)',
        fundShort: '',
        count: 0,
        avg: b.score,
      })),
    [apiSummary],
  );

  const bottomBehaviors = useMemo(
    () =>
      (apiSummary?.opportunities ?? []).map((b, i) => ({
        id: b.id ?? `bot-${i}`,
        name: b.name,
        fundId: '',
        fundColor: 'var(--cp-danger-light)',
        fundShort: '',
        count: 0,
        avg: b.score,
      })),
    [apiSummary],
  );

  // ouScores — directamente desde apiSummary.scoreByOu (sin árbol org local).
  const ouScores = useMemo(
    () =>
      (apiSummary?.scoreByOu ?? []).map((s) => ({
        id: s.ouId,
        name: s.ouId,
        sc: s.score,
        ev: 0,
        om: 0,
        children: [] as Array<{ id: string; n: string; sc?: number; ev?: number; om?: number; children?: unknown[] }>,
      })),
    [apiSummary],
  );

  // omitData — directamente desde apiSummary.omissionsByUnit. El backend hoy solo expone
  // el conteo de omisiones por unidad (`count`), no el total de evaluaciones de esa unidad,
  // por lo que la tasa (`om`) no puede calcularse todavía y se muestra en 0.
  const omitData = useMemo(
    () =>
      (apiSummary?.omissionsByUnit ?? []).map((o) => ({
        id: o.ouId,
        name: o.ouId,
        ev: o.count,
        om: 0,
        people: 0,
        bus: [] as Array<{ id: string; name: string; om: number; ev: number; people: number }>,
      })),
    [apiSummary],
  );

  return (
    <>
      {/* KPIs */}
      <div className="au-kpis">
        {KPI_LIST.map((kpi) => (
          <div key={kpi.label} className="au-kpi">
            <div className="au-kpi-icon" style={{ background: kpi.bg, color: kpi.color }}>
              <i className={`fas ${kpi.icon}`} aria-hidden="true" />
            </div>
            <div className="au-kpi-label">{kpi.label}</div>
            <div
              className="au-kpi-value"
              style={kpi.valueColor ? { color: kpi.valueColor } : {}}
            >
              {kpi.value}
            </div>
            <div className="au-kpi-sub">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Posicionamiento de Fundamentals */}
      <div className="au-card">
        <div className="au-card-header">
          <div>
            <div className="au-card-title">
              <i className="fas fa-medal" aria-hidden="true" />
              {t('analytics.summary.fundPositioning')}
            </div>
            <div className="au-card-sub">{t('analytics.summary.fundPositioningDesc')}</div>
          </div>
        </div>

        {fundStateMessage && (
          <div className={`au-fund-state${isErrorFundamentals ? ' au-fund-state-error' : ''}`}>
            {isLoadingFundamentals && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
            {isErrorFundamentals && <i className="fas fa-triangle-exclamation" aria-hidden="true" />}
            {' '}{fundStateMessage}
          </div>
        )}
        {!fundStateMessage && (
          <>
            {/* Highlight cards */}
            {best && worst && mostNoObs && (
              <div className="au-fund-highlights">
                <div className="au-fund-hl-card" style={{ borderLeft: '4px solid var(--cp-success)' }}>
                  <div
                    className="au-fund-hl-icon"
                    style={{ background: 'var(--cp-success-bg)', color: 'var(--cp-success)' }}
                  >
                    <i className="fas fa-trophy" aria-hidden="true" />
                  </div>
                  <div className="au-fund-hl-body">
                    <div className="au-fund-hl-label">{t('analytics.summary.highestScore')}</div>
                    <div className="au-fund-hl-name">{best.name}</div>
                  </div>
                  <div className="au-fund-hl-score" style={{ color: 'var(--cp-success)' }}>
                    {best.avg}
                  </div>
                </div>
                <div className="au-fund-hl-card" style={{ borderLeft: '4px solid var(--cp-danger)' }}>
                  <div
                    className="au-fund-hl-icon"
                    style={{ background: 'var(--cp-danger-bg)', color: 'var(--cp-danger)' }}
                  >
                    <i className="fas fa-arrow-down" aria-hidden="true" />
                  </div>
                  <div className="au-fund-hl-body">
                    <div className="au-fund-hl-label">{t('analytics.summary.lowestScore')}</div>
                    <div className="au-fund-hl-name">{worst.name}</div>
                  </div>
                  <div className="au-fund-hl-score" style={{ color: 'var(--cp-danger)' }}>
                    {worst.avg}
                  </div>
                </div>
                <div className="au-fund-hl-card" style={{ borderLeft: '4px solid var(--gray-500)' }}>
                  <div
                    className="au-fund-hl-icon"
                    style={{ background: 'var(--surface)', color: 'var(--gray-500)' }}
                  >
                    <i className="fas fa-eye-slash" aria-hidden="true" />
                  </div>
                  <div className="au-fund-hl-body">
                    <div className="au-fund-hl-label">{t('analytics.summary.mostNoObs')}</div>
                    <div className="au-fund-hl-name">{mostNoObs.name}</div>
                  </div>
                  <div className="au-fund-hl-score" style={{ color: 'var(--gray-500)' }}>
                    {mostNoObs.noObsPct}%
                  </div>
                </div>
              </div>
            )}

            {/* Ranking table */}
            <div className="au-fund-rank-table">
              <div className="au-fund-rank-header">
                <span className="au-frh-pos">#</span>
                <span className="au-frh-name">Fundamental</span>
                <span className="au-frh-score">Score</span>
                <span className="au-frh-bar">Distribución</span>
                <span className="au-frh-evals">Evals</span>
                <span className="au-frh-noobs">No Obs.</span>
              </div>
              {fundData.map((f, i) => (
                <div
                  key={f.id}
                  className={[
                    'au-fund-rank-row',
                    i === 0 ? 'au-frr-best' : '',
                    i === fundData.length - 1 ? 'au-frr-worst' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className="au-frr-pos">
                    {i < 3 ? (
                      <i
                        className="fas fa-medal"
                        style={{ color: MEDAL_COLORS[i] }}
                        aria-hidden="true"
                      />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className="au-frr-name">
                    <span className="au-frr-color" style={{ background: f.color }} />
                    {f.name}
                  </span>
                  <span className="au-frr-score" style={{ color: scoreColor(f.avg) }}>
                    {f.avg}
                  </span>
                  <span className="au-frr-bar">
                    <div className="au-frr-bar-track">
                      <div
                        className="au-frr-bar-fill"
                        style={{ width: `${(f.avg / 4) * 100}%`, background: f.color }}
                      />
                    </div>
                  </span>
                  <span className="au-frr-evals">{f.evalCount}</span>
                  <span className={`au-frr-noobs${f.noObsPct > 10 ? ' au-frr-noobs-high' : ''}`}>
                    {f.noObsCount} <small>({f.noObsPct}%)</small>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* OU Score drill */}
      <OUDrillCard ouScores={ouScores} ouDrillId={ouDrillId} setOuDrillId={setOuDrillId} />

      {/* Top / Bottom behaviors */}
      <TopBotBehaviors topBehaviors={topBehaviors} bottomBehaviors={bottomBehaviors} />

      {/* Omissions by OU */}
      <OmissionsCard omitData={omitData} />
    </>
  );
}
